import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, ArrowLeft, ArrowRight, Camera, CheckCircle2, Loader2, Megaphone, RotateCw, Save, Send, X,
} from 'lucide-react';
import {
  ErroreSocial, inviaSocialVerbale, leggiSocialVerbale, preparaSocialVerbale, salvaSocialVerbale, StatoSocial,
} from '../../services/verbaliService';
import {
  contieneUrl, lunghezzaPesataX, MAX_TESTO_SOCIAL, MAX_X, MIN_TESTO_SOCIAL, MIN_X, PREFISSO_PROVA, TESTO_CONSENSO_FOTO,
} from '../../utils/verbaliSocial';
import { useToast } from '../../contexts/ToastContext';

/**
 * Post social del verbale (piano 3) come percorso guidato in una finestra: spiegazione (si apre da
 * sola appena il verbale è pubblicato), foto dell'evento, testo (post e versione per X) e invio in
 * bozza su PostPickr; nel pannello resta solo un riquadro con l'esito per canale e il pulsante per
 * riaprire il percorso. Il tetto di 120 s
 * della risposta del server dice `errore` ma l'invio può essere ancora in volo (il lavoro sul server
 * prosegue fino a 7 minuti): dopo ogni `errore` si rilegge lo stato e «Riprova» compare solo se lo
 * stato riletto è davvero `errore`. «Riprova» riapre la finestra con le spunte vuote: il consenso
 * sulle foto si dà sempre adesso, mai in automatico, e cade se le foto cambiano.
 */

export const TESTO_CONFERMA_NOMI = 'Ho controllato i nomi citati: possono comparire sui social';
export const TESTO_NO_LINK_X = 'Il testo per X non può contenere link';
export const TESTO_FORSE_IN_CORSO = 'L\'invio potrebbe essere ancora in corso: controllo l\'esito tra qualche secondo';
const INTERVALLO_RILETTURA = 3000;
/** Copre il tetto del lavoro sul server (7 minuti) oltre i 120 s della risposta. */
const DURATA_RILETTURA = 8 * 60 * 1000;

/** Messaggi chiari per i codici d'errore dell'invio (altrimenti quello del server). */
const MESSAGGI_INVIO: Record<string, string> = {
  FOTO_MANCANTI: 'Aggiungi almeno una foto prima di inviare il post',
  CONFERMA_NOMI: 'Il testo cita nomi da controllare: conferma che possono comparire sui social',
  INVIO_IN_CORSO: 'L\'invio del post è già in corso',
  GIA_INVIATO: 'Il post è già in bozza su PostPickr',
  TROPPE_RICHIESTE: 'Troppi invii in poco tempo: riprova tra qualche minuto',
  SOCIAL_NON_DISPONIBILE: 'Il post social non è disponibile',
};
const messaggioInvio = (err: ErroreSocial | any): string => {
  if (err?.stato === 429) return err?.messaggio || MESSAGGI_INVIO.TROPPE_RICHIESTE;
  return (err?.codice && MESSAGGI_INVIO[err.codice]) || err?.messaggio || 'Invio del post non riuscito';
};

/** «24/10 alle 08:12» (ora di Roma). */
export function dataOraInvio(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const giorno = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Rome' });
  const ora = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome' });
  return `${giorno} alle ${ora}`;
}

/** Lunghezza del testo per X come la conta il server (con la prova il prefisso entra nel massimo). */
export const contoX = (testoX: string, prova: boolean) =>
  lunghezzaPesataX(testoX.trim()) + (prova ? lunghezzaPesataX(PREFISSO_PROVA) : 0);

interface VerbaleSocialProps {
  eventId: string;
  stato: StatoSocial;
  numeroFoto: number;
  /** Id delle foto: con foto diverse il consenso cade; vanno al server come `fotoConsentite`. */
  fotoIds?: string[];
  onCambia: (stato: StatoSocial) => void;
  /** Verbale appena pubblicato: con il post mai inviato la finestra si apre da sola sulla spiegazione. */
  apriAllaPubblicazione: boolean;
  /** Il caricamento delle foto (passo «foto» del percorso): lo passa la pagina, che tiene le foto. */
  contenutoFoto?: React.ReactNode;
}

/** Passi del percorso: spiegazione, foto, testo e invio. */
export type PassoSocial = 'intro' | 'foto' | 'testo';
export const TESTO_INVIA = 'Invia alla Segreteria Nazionale';

const VerbaleSocial: React.FC<VerbaleSocialProps> = ({
  eventId, stato, numeroFoto, fotoIds, onCambia, apriAllaPubblicazione, contenutoFoto,
}) => {
  const toast = useToast();
  const statoRef = useRef(stato);
  statoRef.current = stato;
  const vivoRef = useRef(true);
  useEffect(() => {
    vivoRef.current = true;
    return () => { vivoRef.current = false; };
  }, []);

  const [salvaRichiesta, setSalvaRichiesta] = useState(false);
  const [aperta, setAperta] = useState(false);
  const [xAperto, setXAperto] = useState(false);
  const [passo, setPasso] = useState<PassoSocial>('intro');
  /** Aperta da sola alla pubblicazione: la spiegazione comincia con «Verbale pubblicato». */
  const [daPubblicazione, setDaPubblicazione] = useState(false);
  const [testo, setTesto] = useState(stato.testo || '');
  const [testoX, setTestoX] = useState(stato.testoX || '');
  const [avvisi, setAvvisi] = useState<string[]>(stato.avvisi || []);
  /**
   * Nomi citati da confermare (seconda spunta): `avvisiNomi` del server o gli `avvisi` di un 409
   * `CONFERMA_NOMI`. Valgono per i testi a cui si riferiscono: si azzerano quando il testo cambia,
   * così un nome aggiunto dopo la conferma fa scattare di nuovo il 409.
   */
  const [avvisiNomi, setAvvisiNomi] = useState<string[]>(stato.avvisiNomi || []);
  const [consenso, setConsenso] = useState(false);
  const [nomiOk, setNomiOk] = useState(false);
  const [preparo, setPreparo] = useState(false);
  const [salvoTesto, setSalvoTesto] = useState(false);
  const [invio, setInvio] = useState(false);
  const [erroreFinestra, setErroreFinestra] = useState('');
  /** Il tetto del server è scattato: l'invio può essere ancora in volo. */
  const [forseInCorso, setForseInCorso] = useState(false);
  const [riletturaScaduta, setRiletturaScaduta] = useState(false);

  // Foto cambiate (caricata, tolta o sostituita): il consenso valeva per le foto di prima.
  const chiaveFoto = fotoIds ? fotoIds.join(',') : `n:${numeroFoto}`;
  const chiaveFotoRef = useRef(chiaveFoto);
  useEffect(() => {
    if (chiaveFotoRef.current === chiaveFoto) return;
    chiaveFotoRef.current = chiaveFoto;
    setConsenso(false);
  }, [chiaveFoto]);

  const pp = stato.postpickr;
  const statoInvio = pp?.stato;
  const bloccato = statoInvio === 'bozza' || statoInvio === 'in_invio';

  // -- Rilettura dello stato mentre l'invio è in corso: ogni 3 s per al più 2 minuti --
  useEffect(() => {
    if (statoInvio !== 'in_invio') return undefined;
    setRiletturaScaduta(false);
    const inizio = Date.now();
    let inVolo = false;
    let annullato = false;
    const timer = setInterval(async () => {
      if (annullato) return;
      if (Date.now() - inizio > DURATA_RILETTURA) {
        clearInterval(timer);
        if (vivoRef.current) setRiletturaScaduta(true);
        return;
      }
      if (inVolo) return;
      inVolo = true;
      try {
        const letto = await leggiSocialVerbale(eventId);
        // Risposta arrivata dopo lo smontaggio o dopo un cambio di stato: si scarta.
        if (!annullato && vivoRef.current && letto) onCambia(letto);
      } catch {
        /* rete momentaneamente giù: si riprova al giro dopo */
      } finally {
        inVolo = false;
      }
    }, INTERVALLO_RILETTURA);
    return () => {
      annullato = true;
      clearInterval(timer);
    };
  }, [statoInvio, eventId, onCambia]);

  useEffect(() => {
    if (statoInvio !== 'in_invio') setForseInCorso(false);
  }, [statoInvio]);

  // -- Proposta --
  const cambiaRichiesta = async (richiesto: boolean) => {
    setSalvaRichiesta(true);
    try {
      const nuovo = await salvaSocialVerbale(eventId, { richiesto });
      if (vivoRef.current && nuovo) onCambia(nuovo);
    } catch (err: any) {
      toast.error('Proposta non salvata', err?.messaggio);
    } finally {
      if (vivoRef.current) setSalvaRichiesta(false);
    }
  };

  // -- Finestra --
  const prepara = useCallback(async () => {
    setPreparo(true);
    setErroreFinestra('');
    try {
      const pronto = await preparaSocialVerbale(eventId);
      if (!vivoRef.current) return;
      setTesto(pronto.testo);
      setTestoX(pronto.testoX);
      setAvvisi(pronto.avvisi);
      setAvvisiNomi(pronto.avvisiNomi || []);
      setNomiOk(false);
      onCambia({
        ...statoRef.current, richiesto: true, testo: pronto.testo, testoX: pronto.testoX, avvisi: pronto.avvisi, avvisiNomi: pronto.avvisiNomi || [],
      });
    } catch (err: any) {
      if (vivoRef.current) setErroreFinestra(err?.messaggio || 'Preparazione del post non riuscita');
    } finally {
      if (vivoRef.current) setPreparo(false);
    }
  }, [eventId, onCambia]);

  /** Apre il percorso dal passo indicato con i testi salvati e le spunte vuote; sul testo vuoto prepara. */
  const apri = useCallback((da: PassoSocial, dopoPubblicazione = false) => {
    const s = statoRef.current;
    setTesto(s.testo || '');
    setTestoX(s.testoX || '');
    setAvvisi(s.avvisi || []);
    setAvvisiNomi(s.avvisiNomi || []);
    setConsenso(false);
    setNomiOk(false);
    setErroreFinestra('');
    setDaPubblicazione(dopoPubblicazione);
    setPasso(da);
    setAperta(true);
    if (da === 'testo' && !(s.testo || '').trim()) prepara();
  }, [prepara]);

  /** Dal riquadro: la spiegazione la prima volta, poi le foto se mancano, altrimenti il testo. */
  const apriDalRiquadro = () => {
    const s = statoRef.current;
    apri(!s.richiesto ? 'intro' : numeroFoto === 0 ? 'foto' : 'testo');
  };

  /** «Sì, proponi un post»: la proposta si registra e si passa alle foto. */
  const accetta = () => {
    if (!statoRef.current.richiesto) cambiaRichiesta(true);
    setPasso('foto');
  };

  const vaiAlTesto = () => {
    setPasso('testo');
    if (!testo.trim() && !preparo) prepara();
  };

  // Verbale appena pubblicato con il post mai inviato: il percorso si apre da solo sulla spiegazione.
  // Si valuta solo al fronte di salita; si parte da «non pubblicato» perché la pagina monta il
  // riquadro proprio alla pubblicazione (in bozza non c'è), già con il segnale acceso.
  const apriRef = useRef(apri);
  apriRef.current = apri;
  const pubblicazionePrimaRef = useRef(false);
  useEffect(() => {
    const salita = apriAllaPubblicazione && !pubblicazionePrimaRef.current;
    pubblicazionePrimaRef.current = apriAllaPubblicazione;
    if (!salita) return;
    if (statoRef.current.postpickr) return;
    apriRef.current('intro', true);
  }, [apriAllaPubblicazione]);

  const chiudi = () => {
    if (invio) return;
    setAperta(false);
  };
  const chiudiRef = useRef(chiudi);
  chiudiRef.current = chiudi;

  // Finestra: Esc chiude, il Tab resta dentro, il focus torna dove era alla chiusura.
  const finestraRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!aperta) return undefined;
    const prima = document.activeElement as HTMLElement | null;
    const focusabili = () => Array.from(
      finestraRef.current?.querySelectorAll<HTMLElement>('button, textarea, input, select, a[href], [tabindex]:not([tabindex="-1"])') || []
    ).filter((el) => !(el as HTMLButtonElement).disabled);
    const primo = focusabili()[0];
    (primo || finestraRef.current)?.focus();
    const alTasto = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        chiudiRef.current();
        return;
      }
      if (e.key !== 'Tab') return;
      const elenco = focusabili();
      if (!elenco.length) {
        e.preventDefault();
        return;
      }
      const attivo = document.activeElement as HTMLElement | null;
      const dentro = !!attivo && !!finestraRef.current?.contains(attivo);
      if (e.shiftKey && (!dentro || attivo === elenco[0])) {
        e.preventDefault();
        elenco[elenco.length - 1].focus();
      } else if (!e.shiftKey && (!dentro || attivo === elenco[elenco.length - 1])) {
        e.preventDefault();
        elenco[0].focus();
      }
    };
    document.addEventListener('keydown', alTasto);
    return () => {
      document.removeEventListener('keydown', alTasto);
      if (prima && typeof prima.focus === 'function' && document.contains(prima)) prima.focus();
    };
  }, [aperta]);

  // Cambio di passo: il fuoco va al titolo della finestra (il pulsante appena premuto sparisce).
  const titoloRef = useRef<HTMLParagraphElement>(null);
  const passoPrimaRef = useRef(passo);
  useEffect(() => {
    if (passoPrimaRef.current === passo) return;
    passoPrimaRef.current = passo;
    if (aperta) titoloRef.current?.focus();
  }, [passo, aperta]);

  /** Testo cambiato: le conferme sui nomi valevano per il testo di prima. */
  const cambiaTesti = (campo: 'testo' | 'testoX', valore: string) => {
    if (campo === 'testo') setTesto(valore);
    else setTestoX(valore);
    setAvvisiNomi([]);
    setNomiOk(false);
  };

  const salvaTesto = async () => {
    setSalvoTesto(true);
    setErroreFinestra('');
    try {
      const nuovo = await salvaSocialVerbale(eventId, { testo, testoX });
      if (!vivoRef.current) return;
      if (nuovo) {
        onCambia(nuovo);
        setAvvisi(nuovo.avvisi || []);
        setAvvisiNomi(nuovo.avvisiNomi || []);
        setNomiOk(false);
      }
      toast.success('Testo del post salvato');
    } catch (err: any) {
      if (vivoRef.current) setErroreFinestra(err?.messaggio || 'Salvataggio del post non riuscito');
    } finally {
      if (vivoRef.current) setSalvoTesto(false);
    }
  };

  /** Dopo un `errore` o un 409 di invio in corso: lo stato vero è quello del server. */
  const rileggiDopoErrore = async (esito: StatoSocial['postpickr'], base: StatoSocial = statoRef.current) => {
    try {
      const letto = await leggiSocialVerbale(eventId);
      if (!vivoRef.current) return;
      if (letto?.postpickr?.stato === 'in_invio') setForseInCorso(true);
      onCambia(letto || { ...base, postpickr: esito });
    } catch {
      if (!vivoRef.current) return;
      // Stato non leggibile: meglio «potrebbe essere in corso» (e rilettura) che un «Riprova» che duplica.
      setForseInCorso(true);
      onCambia({ ...base, postpickr: { ...(esito || { canali: [] }), stato: 'in_invio' } });
    }
  };

  /** Invio dalla finestra: `consensoFoto` solo perché la spunta è stata data adesso (vedi `motivo`). */
  const invia = async (corpo: { testo: string; testoX: string; confermaNomi?: boolean }) => {
    setInvio(true);
    setErroreFinestra('');
    try {
      const esito = await inviaSocialVerbale(eventId, {
        ...corpo, consensoFoto: true, ...(fotoIds && fotoIds.length ? { fotoConsentite: fotoIds } : {}),
      });
      if (!vivoRef.current) return;
      const aggiornato: StatoSocial = { ...statoRef.current, testo: corpo.testo, testoX: corpo.testoX, postpickr: esito };
      if (esito?.stato === 'errore') {
        // Niente «Riprova» prima di aver riletto: col tetto di 120 s il DB resta `in_invio`.
        setAperta(false);
        await rileggiDopoErrore(esito, aggiornato);
        return;
      }
      onCambia(aggiornato);
      setAperta(false);
      if (esito?.stato === 'bozza') toast.success('Post in bozza su PostPickr');
    } catch (err: any) {
      if (!vivoRef.current) return;
      const messaggio = messaggioInvio(err);
      if (err?.codice === 'CONFERMA_NOMI') {
        // Il testo non è cambiato: la spunta del consenso appena data resta, serve solo quella dei nomi.
        setAvvisiNomi(err.avvisi && err.avvisi.length ? err.avvisi : [messaggio]);
        setNomiOk(false);
        setErroreFinestra(messaggio);
        return;
      }
      if (err?.codice === 'CONSENSO_MANCANTE') {
        // Le foto sono cambiate (o il consenso manca): la spunta va data di nuovo.
        setConsenso(false);
        setErroreFinestra(messaggio);
        return;
      }
      if (err?.codice === 'INVIO_IN_CORSO' || err?.codice === 'GIA_INVIATO') {
        setAperta(false);
        toast.info(messaggio);
        await rileggiDopoErrore(statoRef.current.postpickr);
        return;
      }
      setErroreFinestra(messaggio);
      toast.error('Post non inviato', messaggio);
    } finally {
      if (vivoRef.current) setInvio(false);
    }
  };

  /** «Riprova»: il passo del testo con i testi salvati e le spunte vuote (il testo si può correggere). */
  const riprova = () => apri('testo');

  // -- Controlli della finestra --
  const lunghezzaTesto = testo.trim().length;
  /** Con la prova il prefisso entra nel massimo del testo, come lo conta il server. */
  const contoTesto = lunghezzaTesto + (stato.prova ? PREFISSO_PROVA.length : 0);
  const conteggioX = contoX(testoX, stato.prova);
  const xSenzaPrefisso = lunghezzaPesataX(testoX.trim());
  const xConLink = contieneUrl(testoX);
  const serveConfermaNomi = avvisiNomi.length > 0;
  /** Avvisi da mostrare: quelli dei testi più i nomi da confermare, senza doppioni. */
  const avvisiMostrati = Array.from(new Set([...avvisi, ...avvisiNomi]));
  let motivo = '';
  if (numeroFoto === 0) motivo = 'Aggiungi almeno una foto';
  else if (lunghezzaTesto < MIN_TESTO_SOCIAL) motivo = `Il testo del post deve avere almeno ${MIN_TESTO_SOCIAL} caratteri`;
  else if (contoTesto > MAX_TESTO_SOCIAL) motivo = `Il testo del post supera i ${MAX_TESTO_SOCIAL} caratteri`;
  else if (xSenzaPrefisso < MIN_X) motivo = `Il testo per X deve avere almeno ${MIN_X} caratteri`;
  else if (conteggioX > MAX_X) motivo = `Il testo per X supera i ${MAX_X} caratteri di X`;
  else if (xConLink) motivo = TESTO_NO_LINK_X;
  else if (!consenso) motivo = 'Spunta la conferma sulle persone nelle foto';
  else if (serveConfermaNomi && !nomiOk) motivo = 'Conferma di aver controllato i nomi citati';
  const inviabile = !motivo && !invio && !preparo && !salvoTesto;

  // -- Riquadro --
  const esito = () => {
    if (!pp) return null;
    if (statoInvio === 'in_invio') {
      return (
        <div role="status" className="mt-3 rounded-xl bg-sky-50 px-3 py-2.5 text-sm text-sky-900 dark:bg-sky-900/20 dark:text-sky-100">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Invio in corso…
          </p>
          {forseInCorso && <p className="mt-1 text-xs">{TESTO_FORSE_IN_CORSO}</p>}
          {riletturaScaduta && (
            <p className="mt-1 text-xs">Nessun esito ancora: ricarica la pagina tra qualche minuto per vederlo.</p>
          )}
        </div>
      );
    }
    if (statoInvio === 'bozza') {
      const quando = dataOraInvio(pp.inviatoAt);
      return (
        <div className="mt-3 rounded-xl bg-emerald-50 px-3 py-2.5 dark:bg-emerald-900/25">
          <p className="flex items-start gap-2 text-sm font-medium text-emerald-900 dark:text-emerald-100">
            <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
            <span>{quando ? `Post in bozza su PostPickr il ${quando}` : 'Post in bozza su PostPickr'}</span>
          </p>
          {pp.canali.length > 0 && (
            <ul className="mt-2 space-y-1" aria-label="Esito per canale">
              {pp.canali.map((c) => (
                <li key={`${c.id}-${c.tipo}`} className="flex items-start gap-2 text-xs text-slate-700 dark:text-gray-200">
                  <span className="min-w-0 flex-1 truncate">{c.nome || c.tipo}</span>
                  {c.esito === 'ok' ? (
                    <span className="font-semibold text-emerald-700 dark:text-emerald-300">ok</span>
                  ) : (
                    <span className="text-amber-700 dark:text-amber-300">saltato{c.motivo ? `: ${c.motivo}` : ''}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-xs text-emerald-800/80 dark:text-emerald-200/80">La Segreteria Nazionale lo rivede e lo pubblica.</p>
        </div>
      );
    }
    return (
      <div role="alert" className="mt-3 rounded-xl bg-red-50 px-3 py-2.5 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200">
        <p className="flex items-start gap-2 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
          <span>{pp.errore || 'Invio del post non riuscito'}</span>
        </p>
        <button
          type="button"
          onClick={riprova}
          disabled={invio}
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700 disabled:opacity-50"
        >
          {invio ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />}
          Riprova
        </button>
      </div>
    );
  };

  const prova = stato.prova || !!pp?.prova;
  /** Il testo per X si mostra a richiesta, e da solo quando ha qualcosa da correggere. */
  const problemaX = xConLink || conteggioX > MAX_X || (lunghezzaTesto > 0 && xSenzaPrefisso < MIN_X);
  const xVisibile = xAperto || problemaX;

  const titoli: Record<PassoSocial, { titolo: string; sotto: string }> = {
    intro: {
      titolo: daPubblicazione ? 'Verbale pubblicato' : 'Un post per i social di UNASCA',
      sotto: daPubblicazione ? 'Gli invitati sono stati avvisati.' : 'Facoltativo: bastano pochi minuti.',
    },
    foto: { titolo: 'Le foto dell\'evento', sotto: 'Passo 1 di 2' },
    testo: { titolo: 'Il testo del post', sotto: 'Passo 2 di 2' },
  };
  const pulsanteSecondario = 'inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700';
  const pulsantePrimario = 'inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50';

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800" aria-label="Post sui social">
      <div className="mb-2 flex items-center gap-2">
        <Send className="h-4 w-4 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
        <span className="text-sm font-semibold text-slate-900 dark:text-white">Post sui social</span>
        {prova && (
          <span className="ml-auto rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
            Prova (staging)
          </span>
        )}
      </div>

      {!pp && (
        <>
          <p className="text-xs text-slate-500 dark:text-gray-400">
            Proponi alla Segreteria Nazionale un post con le foto dell'evento: lo valuta prima di pubblicarlo sui social di UNASCA.
          </p>
          <button
            type="button"
            onClick={apriDalRiquadro}
            disabled={salvaRichiesta}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Send className="h-4 w-4" aria-hidden="true" />
            {stato.richiesto ? 'Prepara il post' : 'Proponi un post social'}
          </button>
        </>
      )}

      {esito()}

      {aperta && (
        <div
          className="fixed inset-0 z-[9000] flex items-center justify-center bg-slate-900/60 p-3 backdrop-blur-sm sm:p-6"
          onClick={(e) => { if (e.target === e.currentTarget) chiudi(); }}
        >
          <div
            ref={finestraRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label="Post sui social"
            className="flex max-h-full w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-800"
          >
            <div className="flex items-center gap-3 border-b border-slate-200 px-5 py-4 dark:border-gray-700">
              <span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                {passo === 'intro' && daPubblicazione
                  ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                  : passo === 'foto' ? <Camera className="h-4 w-4" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
              </span>
              <div className="min-w-0 flex-1">
                <p ref={titoloRef} tabIndex={-1} className="text-base font-semibold text-slate-900 focus:outline-none dark:text-white">
                  {titoli[passo].titolo}
                </p>
                <p className="text-xs text-slate-500 dark:text-gray-400">{titoli[passo].sotto}</p>
              </div>
              {stato.prova && (
                <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                  Prova (staging)
                </span>
              )}
              <button
                type="button"
                onClick={chiudi}
                disabled={invio}
                aria-label="Chiudi"
                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-40 dark:hover:bg-gray-700"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
              {passo === 'intro' && (
                <div className="space-y-3 text-sm text-slate-700 dark:text-gray-200">
                  <p className="flex items-start gap-2 rounded-xl bg-emerald-50 px-3 py-2.5 text-sm font-medium text-emerald-900 dark:bg-emerald-900/25 dark:text-emerald-100">
                    <Megaphone className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                    <span>Novità: puoi proporre un post sui social della Segreteria Nazionale per raccontare questo evento.</span>
                  </p>
                  <p className="text-sm">Ti chiediamo solo due cose:</p>
                  <ol className="list-decimal space-y-1 pl-5">
                    <li>qualche <strong>foto dell'evento</strong> (da 1 a 5);</li>
                    <li>un'occhiata al <strong>testo del post</strong>, che prepariamo noi partendo dal verbale.</li>
                  </ol>
                  <p className="text-sm">
                    Il post non esce subito: arriva alla <strong>Segreteria Nazionale</strong>, che lo valuta prima di condividerlo sui social nazionali.
                  </p>
                  <p className="text-sm">Serve a dare visibilità a UNASCA e anche alla tua segreteria provinciale.</p>
                  <p className="text-xs text-slate-500 dark:text-gray-400">
                    Se preferisci farlo più tardi, trovi il pulsante «Proponi un post social» nella pagina del verbale.
                  </p>
                </div>
              )}

              {passo === 'foto' && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-700 dark:text-gray-200">
                    Premi <strong>+</strong> e scegli da 1 a 5 foto dell'evento, dal computer o dal telefono. Le foto non entrano nel verbale: servono solo al post.
                  </p>
                  {contenutoFoto}
                </div>
              )}

              {passo === 'testo' && (
                <>
              <p className="text-sm text-slate-700 dark:text-gray-200">
                Il testo lo prepariamo noi partendo dal verbale: leggilo, correggi se serve, poi spunta la conferma sulle foto e invia.
              </p>
              {preparo && (
                <p role="status" className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Sto preparando il post…
                </p>
              )}
              <div>
                <label htmlFor="verbale-social-testo" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-gray-200">
                  Testo del post
                </label>
                <textarea
                  id="verbale-social-testo"
                  value={testo}
                  onChange={(e) => cambiaTesti('testo', e.target.value)}
                  rows={8}
                  disabled={preparo || invio}
                  className="w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                />
                <p className={`mt-1 text-right text-xs ${contoTesto > MAX_TESTO_SOCIAL ? 'font-semibold text-red-600 dark:text-red-400' : 'text-slate-400'}`}>
                  {stato.prova && <span className="mr-2 text-slate-500 dark:text-gray-400">Comprende il prefisso della prova.</span>}
                  {contoTesto}/{MAX_TESTO_SOCIAL}
                </p>
              </div>
              <div>
                <button
                  type="button"
                  onClick={() => setXAperto((v) => !v)}
                  aria-expanded={xVisibile}
                  className="text-xs font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-800 dark:text-emerald-300"
                >
                  {xVisibile ? 'Nascondi il testo corto per X' : `Testo corto per X (${conteggioX}/${MAX_X}): mostra e modifica`}
                </button>
              </div>
              <div className={xVisibile ? '' : 'hidden'}>
                <label htmlFor="verbale-social-testo-x" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-gray-200">
                  Testo per X
                </label>
                <textarea
                  id="verbale-social-testo-x"
                  value={testoX}
                  onChange={(e) => cambiaTesti('testoX', e.target.value)}
                  rows={3}
                  disabled={preparo || invio}
                  className="w-full resize-y rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
                />
                <div className="mt-1 flex items-start gap-2 text-xs">
                  <span className="min-w-0 flex-1 text-slate-500 dark:text-gray-400">
                    {xConLink
                      ? <span className="font-medium text-red-600 dark:text-red-400">{TESTO_NO_LINK_X}</span>
                      : stato.prova ? 'Il conteggio comprende il prefisso della prova.' : 'Senza link: X conta alcuni caratteri ed emoji come 2.'}
                  </span>
                  <span className={conteggioX > MAX_X ? 'font-semibold text-red-600 dark:text-red-400' : 'text-slate-400'} data-testid="conteggio-x">
                    {conteggioX}/{MAX_X}
                  </span>
                </div>
              </div>

              {avvisiMostrati.length > 0 && (
                <ul className="space-y-1.5" aria-label="Avvisi sul testo">
                  {avvisiMostrati.map((a) => (
                    <li key={a} className="flex items-start gap-2 rounded-lg bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={prepara}
                  disabled={preparo || invio}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                  Rigenera
                </button>
                <button
                  type="button"
                  onClick={salvaTesto}
                  disabled={preparo || invio || salvoTesto}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                >
                  {salvoTesto ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Save className="h-3.5 w-3.5" aria-hidden="true" />}
                  Salva testo
                </button>
              </div>

              <div className="space-y-2 rounded-xl border border-slate-200 p-3 dark:border-gray-700">
                <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={consenso}
                    onChange={(e) => setConsenso(e.target.checked)}
                    disabled={invio}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-gray-600"
                  />
                  <span>{TESTO_CONSENSO_FOTO}</span>
                </label>
                {serveConfermaNomi && (
                  <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-gray-200">
                    <input
                      type="checkbox"
                      checked={nomiOk}
                      onChange={(e) => setNomiOk(e.target.checked)}
                      disabled={invio}
                      className="mt-0.5 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-gray-600"
                    />
                    <span>{TESTO_CONFERMA_NOMI}</span>
                  </label>
                )}
              </div>

              {erroreFinestra && (
                <p role="alert" className="flex items-start gap-2 rounded-lg bg-red-50 px-2.5 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                  <span>{erroreFinestra}</span>
                </p>
              )}
                </>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 py-3 dark:border-gray-700">
              {passo === 'intro' && (
                <>
                  <button type="button" onClick={chiudi} className={pulsanteSecondario}>
                    Non ora
                  </button>
                  <button type="button" onClick={accetta} className={pulsantePrimario}>
                    Sì, proponi un post
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </>
              )}
              {passo === 'foto' && (
                <>
                  {numeroFoto === 0 && <span className="mr-auto text-xs text-slate-500 dark:text-gray-400">Aggiungi almeno una foto</span>}
                  <button type="button" onClick={() => setPasso('intro')} className={pulsanteSecondario}>
                    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                    Indietro
                  </button>
                  <button type="button" onClick={vaiAlTesto} disabled={numeroFoto === 0} className={pulsantePrimario}>
                    Avanti
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </button>
                </>
              )}
              {passo === 'testo' && (
                <>
                  {motivo && !preparo && <span className="mr-auto text-xs text-slate-500 dark:text-gray-400">{motivo}</span>}
                  <button type="button" onClick={() => setPasso('foto')} disabled={invio} className={pulsanteSecondario}>
                    <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                    Indietro
                  </button>
                  <button
                    type="button"
                    onClick={() => invia({ testo: testo.trim(), testoX: testoX.trim(), ...(serveConfermaNomi && nomiOk ? { confermaNomi: true } : {}) })}
                    disabled={!inviabile}
                    className={pulsantePrimario}
                  >
                    {invio ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
                    {invio ? 'Invio in corso…' : TESTO_INVIA}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default VerbaleSocial;
